import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../firebase";
import {
  collection,
  writeBatch,
  serverTimestamp,
  addDoc,
  getDocs,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
  getDoc,
  query,
  where,
} from "firebase/firestore";
import { startOfWeek, parseISO, addDays, isWithinInterval, format } from "date-fns";

// ────────────────────────────────
// Notification helpers (Spark-safe)
// ────────────────────────────────

async function getEmailByAuthUid(authUid) {
  if (!authUid) return null;

  const q = query(collection(db, "users"), where("authUid", "==", authUid));

  const snap = await getDocs(q);
  if (snap.empty) return null;

  return snap.docs[0].data()?.email || null;
}

async function createShiftAssignedNotification({ shiftId, staffAuthUid, shiftData }) {
  const staffEmail = await getEmailByAuthUid(staffAuthUid);
  if (!staffEmail) {
    console.warn("No email found for authUid:", staffAuthUid);
    return;
  }

  const notifRef = doc(db, "notifications", staffEmail, "items", `shift_${shiftId}_assigned`);

  const body = [
    shiftData?.date && `Date: ${shiftData.date}`,
    shiftData?.startTime && `Start: ${shiftData.startTime}`,
    shiftData?.endTime && `End: ${shiftData.endTime}`,
    shiftData?.clientName && `Client: ${shiftData.clientName}`,
  ]
    .filter(Boolean)
    .join(" • ");

  await setDoc(
    notifRef,
    {
      type: "shift_assigned",
      title: "New shift assigned",
      body,
      read: false,
      shiftId,
      createdAt: serverTimestamp(),
      data: { shiftId, actionUrl: `/shift/${shiftId}` },
      source: { kind: "shift", event: "assigned", id: shiftId },
    },
    { merge: true }
  );
}

export default function ShiftsEditor() {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [staff, setStaff] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [allShifts, setAllShifts] = useState([]); // store everything initially
  const [filterType, setFilterType] = useState("week");
  const [selectedPeriod, setSelectedPeriod] = useState("");
  const [selectedClient, setSelectedClient] = useState("all");
  const [selectedStaff, setSelectedStaff] = useState("all");
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Modal state
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [shiftToAssign, setShiftToAssign] = useState(null);
  const [selectedRequester, setSelectedRequester] = useState(null);

  const [extendEndByHour, setExtendEndByHour] = useState(false);

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Lists for dropdown filters
  //const clientList = [...new Set(allShifts.map((s) => s.client))];
  //const staffList = [...new Set(allShifts.map((s) => s.staff))];

  //  NEW LINES (Keeps them as strings so your JSX won't crash!)
  const clientList = clients.map((c) => c.fullName).filter(Boolean);
  const staffList = staff.map((s) => s.fullName).filter(Boolean);

  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [newShift, setNewShift] = useState({
    id: "",
    clientId: "",
    staffId: "",
    date: "",
    startTime: "",
    endTime: "",
    notes: "",
    rate: "",
    isOpen: false,
  });

  // ────────────────────────────────
  // Load data from Firestore
  // ────────────────────────────────
  useEffect(() => {
    const loadData = async () => {
      const clientsSnap = await getDocs(collection(db, "clients"));
      const staffSnap = await getDocs(collection(db, "staff"));
      const shiftsSnap = await getDocs(collection(db, "shifts"));
      const rotasSnap = await getDocs(collection(db, "rotas"));

      // Convert docs to JS objects
      const clientsData = clientsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const staffData = staffSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
      const shiftsData = shiftsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

      // 🧩 Extract all assignments from rota docs
      let rotaAssignments = [];
      rotasSnap.docs.forEach((rotaDoc) => {
        const data = rotaDoc.data();
        if (data.assignments && Array.isArray(data.assignments)) {
          data.assignments.forEach((a) => {
            rotaAssignments.push({
              id: a.shiftId || `${rotaDoc.id}_${a.date}`,
              source: "rota",
              ...a,
            });
          });
        }
      });

      // 🧮 Merge shifts + rotaAssignments by shiftId (avoid duplicates)
      const mergedShifts = [
        ...shiftsData,
        ...rotaAssignments.filter((ra) => !shiftsData.some((s) => s.id === ra.id)),
      ];

      // ✅ Update state
      setClients(clientsData);
      setStaff(staffData);
      setShifts(mergedShifts);
      setAllShifts(mergedShifts);
      setLoading(false);
    };

    loadData();
  }, []);

  const openAssignModal = (shift) => {
    setShiftToAssign(shift);
    setAssignModalOpen(true);
  };

  const filterShifts = () => {
    let filtered = allShifts;

    // 1️⃣ WEEK FILTER
    if (filterType === "week") {
      if (!selectedPeriod) {
        setShifts(allShifts);
        return;
      }

      const selectedDate = new Date(selectedPeriod);
      const weekStart = startOfWeek(selectedDate, { weekStartsOn: 1 });
      const weekEnd = addDays(weekStart, 6);

      filtered = filtered.filter((s) => {
        const shiftDate = new Date(s.date);
        return shiftDate >= weekStart && shiftDate <= weekEnd;
      });
    }

    // 2️⃣ MONTH FILTER
    else if (filterType === "month") {
      if (!selectedPeriod) {
        setShifts(allShifts);
        return;
      }

      const [year, month] = selectedPeriod.split("-");
      filtered = filtered.filter((s) => {
        const d = new Date(s.date);
        return d.getFullYear() === parseInt(year) && d.getMonth() + 1 === parseInt(month);
      });
    }

    // 3️⃣ DATE RANGE FILTER
    else if (filterType === "range") {
      if (startDate) {
        const start = new Date(startDate);
        filtered = filtered.filter((s) => new Date(s.date) >= start);
      }

      if (endDate) {
        const end = new Date(endDate);
        filtered = filtered.filter((s) => new Date(s.date) <= end);
      }
    }

    // 4️⃣ CLIENT FILTER
    //if (selectedClient && selectedClient !== "all") {
    //  filtered = filtered.filter((s) => s.client?.toLowerCase() === selectedClient.toLowerCase());
    //}

    if (selectedClient && selectedClient !== "all") {
      filtered = filtered.filter((s) => s.clientName?.toLowerCase() === selectedClient.toLowerCase());
    }

    // 5️⃣ STAFF FILTER
   // if (selectedStaff && selectedStaff !== "all") {
    //  filtered = filtered.filter((s) => s.staff?.toLowerCase() === selectedStaff.toLowerCase());
    //}

    if (selectedStaff && selectedStaff !== "all") {
      filtered = filtered.filter((s) => s.staffName?.toLowerCase() === selectedStaff.toLowerCase());
    }

    // 6️⃣ STATUS FILTER
    //if (selectedStatus && selectedStatus !== "all") {
    //  filtered = filtered.filter((s) => s.status?.toLowerCase() === selectedStatus.toLowerCase());
    //}


    if (selectedStatus && selectedStatus !== "all") {
      filtered = filtered.filter((s) => s.status?.toLowerCase() === selectedStatus.toLowerCase());
    }

    setShifts(filtered);
  };

  // ────────────────────────────────
  // Week key helpers
  // ────────────────────────────────
  const getWeekKey = (isoDate) => {
    const weekStart = startOfWeek(new Date(isoDate), { weekStartsOn: 1 });
    return {
      weekStartISO: format(weekStart, "yyyy-MM-dd"),
      weekEndISO: format(addDays(weekStart, 6), "yyyy-MM-dd"),
      docId: `week_${format(weekStart, "yyyy-MM-dd")}`,
    };
  };

  // ────────────────────────────────
  // Add or Update Shift + Sync to Rota
  // ────────────────────────────────
  const handleSubmitShift = async (e) => {
    e.preventDefault();

    // Basic validation
    if (!newShift.clientId || !newShift.date) return alert("Please fill all required fields.");

    if (!newShift.isOpen && !newShift.staffId) {
      return alert("Please assign a staff member or mark the shift as available.");
    }

    const client = clients.find((c) => c.id === newShift.clientId);
    if (!client) {
      return alert("Missing client details.");
    }

    let staffMember = null;
    if (!newShift.isOpen && newShift.staffId) {
      staffMember = staff.find((s) => s.id === newShift.staffId);
      if (!staffMember) {
        return alert("Missing staff details.");
      }
    }

    // 🕒 Calculate total hours (with optional +1h extension)
    const start = new Date(`${newShift.date}T${newShift.startTime}`);

    let end = new Date(`${newShift.date}T${newShift.endTime}`);
    if (extendEndByHour) {
      end = new Date(end.getTime() + 60 * 60 * 1000); // +1 hour
    }

    const hours = (end - start) / (1000 * 60 * 60);
    if (hours <= 0) return alert("End time must be after start time.");

    // 🧠 Load staff record to include authUid (only if assigned)
    let staffData = {};
    if (!newShift.isOpen && newShift.staffId) {
      try {
        const staffRef = doc(db, "staff", newShift.staffId);
        const staffSnap = await getDoc(staffRef);
        if (staffSnap.exists()) staffData = staffSnap.data();
      } catch (err) {
        console.error("Error fetching staff record:", err);
      }
    }

    // ⭐ NEW — Geocode client postcode
    const geocodePostcode = async (postcode) => {
      const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(
        postcode
      )}`;
      const res = await fetch(url);
      const data = await res.json();
      if (!data.length) return null;
      return {
        lat: parseFloat(data[0].lat),
        lng: parseFloat(data[0].lon),
      };
    };

    const clientPostcode = client.postcode;
    const clientLocation = await geocodePostcode(clientPostcode);

    if (!clientLocation) {
      alert("Unable to find map location for client postcode.");
      return;
    }

    // Build the shift object
    const shiftData = {
      clientId: newShift.clientId,
      clientName: client.fullName,

      // ⭐ Client location fields
      clientPostcode: clientPostcode,
      clientLocation: clientLocation,

      // 🔹 Staff only if not open
      staffId: newShift.isOpen ? null : newShift.staffId,
      staffName: newShift.isOpen ? null : staffMember?.fullName || null,
      authUid: newShift.isOpen ? null : staffData.authUid || null,

      date: newShift.date,

      startTime: newShift.startTime,

      // 🧠 NEW FIELDS
      plannedEndTime: newShift.endTime,
      extendEndByHour,

      // 🔥 Actual checkout cutoff
      endTime: (() => {
        if (!extendEndByHour) return newShift.endTime;

        const [h, m] = newShift.endTime.split(":").map(Number);
        const totalMinutes = h * 60 + m + 60;
        const hh = String(Math.floor(totalMinutes / 60) % 24).padStart(2, "0");
        const mm = String(totalMinutes % 60).padStart(2, "0");
        return `${hh}:${mm}`;
      })(),

      totalHours: hours,

      
      rate: newShift.rate !== "" ? Number(newShift.rate) : staffMember?.rate ?? null,


      status: newShift.isOpen ? "Open" : "Assigned",
      notes: newShift.notes,
      isOpen: newShift.isOpen || false, // 🔹 Save flag to Firestore too
    };

    try {
      let shiftId = newShift.id;

      if (editing && shiftId) {
        // Update existing shift
        await updateDoc(doc(db, "shifts", shiftId), shiftData);
        setShifts(shifts.map((s) => (s.id === shiftId ? { ...s, ...shiftData } : s)));
        alert("✅ Shift updated successfully!");
      } else {
        // Add new shift
        const ref = await addDoc(collection(db, "shifts"), shiftData);
        shiftId = ref.id;
        setShifts([...shifts, { id: ref.id, ...shiftData }]);
        alert("✅ Shift added successfully!");
      }

      // 🔔 Create notification ONLY if shift is assigned (not open)
      if (!newShift.isOpen && staffData?.authUid) {
        await createShiftAssignedNotification({
          shiftId,
          staffAuthUid: staffData.authUid,
          shiftData,
        });
      }

      // ────────────────────────────────
      // Rota Sync (only for assigned shifts)
      // ────────────────────────────────
      if (!newShift.isOpen) {
        try {
          const rotaRef = doc(db, "rotas", newShift.date);
          const rotaSnap = await getDoc(rotaRef);

          const rotaShift = {
            shiftId,
            staffId: newShift.staffId,
            staffName: staffMember.fullName,
            authUid: staffData.authUid || null,
            clientName: client.fullName,
            date: newShift.date,
            startTime: newShift.startTime,
            endTime: newShift.endTime,
            status: "Assigned",
            rate: shiftData.rate,
          };

          if (rotaSnap.exists()) {
            const existing = rotaSnap.data();
            const updatedShifts = Array.isArray(existing.shifts)
              ? [...existing.shifts.filter((s) => s.shiftId !== shiftId), rotaShift]
              : [rotaShift];

            await updateDoc(rotaRef, { shifts: updatedShifts });
          } else {
            await setDoc(rotaRef, {
              date: newShift.date,
              shifts: [rotaShift],
              createdAt: new Date().toISOString(),
            });
          }
        } catch (rotaErr) {
          console.error("Error syncing shift to rota:", rotaErr);
        }
      }
    } catch (error) {
      console.error("Error saving shift:", error);
      alert("❌ Failed to save shift. Check console for details.");
    }

    // Reset form
    setNewShift({
      id: "",
      clientId: "",
      staffId: "",
      date: "",
      startTime: "",
      endTime: "",
      notes: "",
      rate: "",
      isOpen: false,
    });

    setEditing(false);
    setExtendEndByHour(false);
  };

  // ────────────────────────────────
  // Delete Shift
  // ────────────────────────────────
  const handleDeleteShift = async (shiftId) => {
    try {
      const shift = shifts.find((s) => s.id === shiftId);
      if (!shift) return alert("Shift not found.");
      if (!window.confirm(`Delete shift for ${shift.staffName || "Unknown"} on ${shift.date}?`)) return;

      // 1️⃣ Delete from "shifts" collection
      try {
        await deleteDoc(doc(db, "shifts", shiftId));
        console.log(`Deleted from shifts/${shiftId}`);
      } catch (err) {
        console.log("Shift not in main collection, skipping…");
      }

      // 2️⃣ Delete from any rota week containing this shift
      const rotasSnap = await getDocs(collection(db, "rotas"));
      for (const rotaDoc of rotasSnap.docs) {
        const data = rotaDoc.data();
        if (data.assignments && Array.isArray(data.assignments)) {
          const filtered = data.assignments.filter((a) => a.shiftId !== shiftId);
          if (filtered.length !== data.assignments.length) {
            await setDoc(rotaDoc.ref, { assignments: filtered }, { merge: true });
            console.log(`Removed shift from rota ${rotaDoc.id}`);
          }
        }
      }

      // 3️⃣ Delete from timesheets (search by staffId and week)
      if (shift.staffId && shift.date) {
        const { weekStartISO } = getWeekKey(shift.date);
        const tsId = `${shift.staffId}_week_${weekStartISO}`;
        const tsRef = doc(db, "timesheets", tsId);
        const tsSnap = await getDoc(tsRef);

        if (tsSnap.exists()) {
          const ts = tsSnap.data();
          const updatedEntries = (ts.entries || []).filter((e) => e.shiftId !== shiftId);
          const totalHours = updatedEntries.reduce((sum, e) => sum + (e.hours || 0), 0);
          const totalPay = updatedEntries.reduce((sum, e) => sum + (e.hours || 0) * (e.rate || 0), 0);
          const status = updatedEntries.length
            ? updatedEntries.every((e) => e.approved)
              ? "Approved"
              : "Pending"
            : "Empty";

          await setDoc(tsRef, { entries: updatedEntries, totalHours, totalPay, status }, { merge: true });
          console.log(`Removed shift from timesheet ${tsId}`);
        }
      }

      // 4️⃣ Update local state
      setShifts(shifts.filter((s) => s.id !== shiftId));

      alert("🟧 Shift deleted everywhere (shifts, rota, and timesheet).");
    } catch (err) {
      console.error("❌ Error deleting shift:", err);
      alert("Error deleting shift. Check console for details.");
    }
  };

  const approveShift = async (shift) => {
    try {
      // find who requested it
      const q = query(collection(db, "staff"), where("email", "==", shift.requestedBy.toLowerCase()));
      const snap = await getDocs(q);

      if (snap.empty) {
        alert("Requester not found in staff database.");
        return;
      }

      const staffDoc = snap.docs[0];
      const staffData = staffDoc.data();

      await updateDoc(doc(db, "shifts", shift.id), {
        staffId: staffDoc.id,
        staffName: staffData.fullName || staffData.name,
        authUid: staffData.authUid || null,
        status: "Assigned",
        isOpen: false,
      });

      alert("Shift approved & assigned!");

      setShifts((prev) =>
        prev.map((s) =>
          s.id === shift.id
            ? {
                ...s,
                staffId: staffDoc.id,
                staffName: staffData.fullName || staffData.name,
                status: "Assigned",
                isOpen: false,
              }
            : s
        )
      );
    } catch (err) {
      console.error("Error approving shift:", err);
      alert("Error approving shift");
    }
  };

  const assignRequestedShift = async () => {
    if (!shiftToAssign || !selectedRequester) {
      alert("Please select a staff member to assign.");
      return;
    }

    try {
      // Convert from our requester format to actual staff record
      const q = query(collection(db, "staff"), where("email", "==", selectedRequester.email.toLowerCase()));

      const snap = await getDocs(q);
      if (snap.empty) {
        alert("Cannot find this staff member in staff records.");
        return;
      }

      const staffDoc = snap.docs[0];
      const staffData = staffDoc.data();

      // 🔥 Update shift as assigned
      await updateDoc(doc(db, "shifts", shiftToAssign.id), {
        staffId: staffDoc.id,
        staffName: staffData.fullName || staffData.name,
        authUid: staffData.authUid || null,
        status: "Assigned",
        isOpen: false,
        assignedAt: new Date().toISOString(),
      });

      // 🔥 Update local UI
      setShifts((prev) =>
        prev.map((s) =>
          s.id === shiftToAssign.id
            ? {
                ...s,
                staffId: staffDoc.id,
                staffName: staffData.fullName || staffData.name,
                status: "Assigned",
                isOpen: false,
              }
            : s
        )
      );

      alert("Shift assigned successfully!");
      setAssignModalOpen(false);
      setSelectedRequester(null);
    } catch (err) {
      console.error("Assignment error:", err);
      alert("Error assigning shift.");
    }
  };

  // ────────────────────────────────
  // Status color utility
  // ────────────────────────────────
  const getStatusColor = (status) => {
    switch (status) {
      case "Assigned":
        return "green";
      case "Open":
        return "#0284c7"; // blue for available shifts
      case "Requested":
        return "#7c3aed"; // purple for requested
      case "Completed":
        return "orange";
      case "Cancelled":
      case "Deleted":
        return "red";
      default:
        return "gray";
    }
  };

  // ────────────────────────────────
  // Loading state
  // ────────────────────────────────
  if (loading) return <p style={{ textAlign: "center", color: "#aaa" }}>Loading...</p>;

  // ────────────────────────────────
  // Convalesce styling (matching InvoiceManager look)
  // ────────────────────────────────
  const pageWrap = { paddingTop: "20px" };
  const pageClass = "p-6 pt-20 min-h-screen bg-gray-700/80 text-gray-100";

  const headerPill = {
    textAlign: "center",
    color: "gray",
    backgroundColor: "white",
    boxShadow: "0 4px 10px rgba(0,0,0,0.15)",
    width: "fit-content",
    margin: "10px auto 22px auto",
    fontSize: "16px",
    fontWeight: "500",
    padding: "14px 24px",
    borderRadius: "20px",
  };

  const card = {
    maxWidth: "1100px",
    margin: "18px auto",
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "24px",
    color: "#1f2937",
    boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
  };

  const cardNarrow = {
    maxWidth: "900px",
    margin: "18px auto",
    backgroundColor: "white",
    borderRadius: "16px",
    padding: "24px",
    color: "#1f2937",
    boxShadow: "0 4px 8px rgba(0,0,0,0.05)",
  };

  const innerCard = {
    border: "1px solid #e5e7eb",
    borderRadius: "16px",
    padding: "16px",
    backgroundColor: "#f9fafb",
  };

  const label = { fontSize: "12px", color: "#6b7280", fontWeight: 700, marginBottom: 6 };

  const inputStyle = {
    width: "100%",
    border: "1px solid #d1d5db",
    borderRadius: "10px",
    padding: "10px 12px",
    fontSize: "13px",
    outline: "none",
    backgroundColor: "#fff",
  };

  const selectStyle = { ...inputStyle };

  const btn = (bg) => ({
    padding: "10px 12px",
    borderRadius: "10px",
    backgroundColor: bg,
    color: "#fff",
    fontWeight: "700",
    border: "none",
    cursor: "pointer",
  });

  const btnFull = (bg) => ({ ...btn(bg), width: "100%", padding: "12px" });

  const tdStyle = {
    padding: "12px 10px",
    borderBottom: "1px solid #e5e7eb",
    fontSize: "13px",
    color: "#111827",
    verticalAlign: "top",
  };

  const thStyle = {
    textAlign: "left",
    fontSize: "12px",
    color: "#1d4ed8",
    background: "#f9fafb",
    borderBottom: "1px solid #e5e7eb",
    padding: "10px 10px",
    fontWeight: 900,
    whiteSpace: "nowrap",
  };

  const actionButton = (color) => ({
    backgroundColor: color,
    color: "white",
    border: "none",
    padding: "8px 10px",
    margin: "0 4px",
    borderRadius: "10px",
    cursor: "pointer",
    fontWeight: 700,
  });

  // ────────────────────────────────
  // UI
  // ────────────────────────────────
  return (
    
<div style={pageWrap} className={pageClass}>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "10px", marginBottom: "12px" }}>
        <h1 style={{ ...headerPill, margin: "10px 0 0 0" }}>Shift Management</h1>

{/* NEW ACTION BUTTON */}
<button 
  type="button" // <--- CRITICAL FIX: Stops the form from submitting and reloading the app!
  onClick={() => navigate("/admin/shift-status")}
  style={{
    ...btn("#10b981"), 
    fontSize: "13px",
    padding: "8px 16px",
    boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)"
  }}
>
  🔄 Switch to Shift Status Manager
</button>


    
      </div>

      {/* Card - Add / Edit Shift */}
      <div style={cardNarrow}>
        <h2 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "14px", color: "#111827" }}>
          {editing ? "Edit Shift" : "Add New Shift"}
        </h2>

        <div style={innerCard}>
          <form
            onSubmit={handleSubmitShift}
            style={{ display: "grid", gap: "12px", gridTemplateColumns: "1fr 1fr" }}
          >
            <div>
              <div style={label}>Client</div>
              <select
                value={newShift.clientId}
                onChange={(e) => setNewShift({ ...newShift, clientId: e.target.value })}
                style={selectStyle}
              >
                <option value="">Select Client</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={label}>Assign Staff</div>
              <select
                value={newShift.staffId}
                onChange={(e) => setNewShift({ ...newShift, staffId: e.target.value })}
                style={selectStyle}
                disabled={newShift.isOpen}
              >
                <option value="">Assign Staff</option>
                {staff.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.fullName}
                  </option>
                ))}
              </select>
            </div>

            <div style={{ gridColumn: "1 / -1", display: "grid", gap: 8 }}>
              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "13px",
                  color: "#374151",
                  fontWeight: 700,
                }}
              >
                <input
                  type="checkbox"
                  checked={newShift.isOpen || false}
                  onChange={(e) =>
                    setNewShift((prev) => ({
                      ...prev,
                      isOpen: e.target.checked,
                      staffId: e.target.checked ? "" : prev.staffId,
                    }))
                  }
                />
                Make this shift available to all staff (not assigned)
              </label>

              <label
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  fontSize: "13px",
                  color: "#374151",
                  fontWeight: 700,
                }}
              >
                <input
                  type="checkbox"
                  checked={extendEndByHour}
                  onChange={(e) => setExtendEndByHour(e.target.checked)}
                />
                Allow sign-out up to 1 hour after end time
              </label>
            </div>

            <div>
              <div style={label}>Date</div>
              <input
                type="date"
                value={newShift.date}
                onChange={(e) => setNewShift({ ...newShift, date: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={label}>Rate (£/hr)</div>
              <input
                type="number"
                placeholder="Rate (£/hr)"
                value={newShift.rate}
                onChange={(e) => setNewShift({ ...newShift, rate: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={label}>Start time</div>
              <input
                type="time"
                value={newShift.startTime}
                onChange={(e) => setNewShift({ ...newShift, startTime: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div>
              <div style={label}>End time</div>
              <input
                type="time"
                value={newShift.endTime}
                onChange={(e) => setNewShift({ ...newShift, endTime: e.target.value })}
                style={inputStyle}
              />
            </div>

            <div style={{ gridColumn: "1 / -1" }}>
              <div style={label}>Notes</div>
              <input
                type="text"
                placeholder="Notes"
                value={newShift.notes}
                onChange={(e) => setNewShift({ ...newShift, notes: e.target.value })}
                style={inputStyle}
              />
            </div>

            <button type="submit" style={{ ...btnFull("#2563eb"), gridColumn: "1 / -1" }}>
              {editing ? "Update Shift" : "Add Shift"}
            </button>
          </form>
        </div>
      </div>

      {/* Filter Section */}
      <div style={card}>
        <h2 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "14px", color: "#111827" }}>
          Filters
        </h2>

        <div style={innerCard}>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr 1fr",
              gap: 12,
              alignItems: "end",
            }}
          >
            <div>
              <div style={label}>Filter by</div>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} style={selectStyle}>
                <option value="week">Week</option>
                <option value="month">Month</option>
                <option value="range">Date Range</option>
              </select>
            </div>

            {filterType !== "range" && (
              <div>
                <div style={label}>{filterType === "month" ? "Month" : "Week (pick a date)"}</div>
                <input
                  type={filterType === "month" ? "month" : "date"}
                  value={selectedPeriod}
                  onChange={(e) => setSelectedPeriod(e.target.value)}
                  style={inputStyle}
                />
              </div>
            )}

            {filterType === "range" && (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={label}>From</div>
                  <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <div style={label}>To</div>
                  <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} style={inputStyle} />
                </div>
              </div>
            )}

            <div>
              <div style={label}>Client</div>
              <select value={selectedClient} onChange={(e) => setSelectedClient(e.target.value)} style={selectStyle}>
                <option value="all">All Clients</option>
                {clientList.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={label}>Staff</div>
              <select value={selectedStaff} onChange={(e) => setSelectedStaff(e.target.value)} style={selectStyle}>
                <option value="all">All Staff</option>
                {staffList.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <div style={label}>Status</div>
              <select value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)} style={selectStyle}>
                <option value="all">All Statuses</option>
                <option value="pending">Assigned</option>
                <option value="completed">Checked Out</option>
                <option value="completed">Checked In</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
            <button onClick={filterShifts} style={btn("#2563eb")}>
              Apply Filter
            </button>

            <button
              onClick={() => {
                setSelectedPeriod("");
                setStartDate("");
                setEndDate("");
                setShifts(allShifts);
              }}
              style={btn("#6b7280")}
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div style={card}>
        <h2 style={{ fontSize: "16px", fontWeight: 700, marginBottom: "14px", color: "#111827" }}>
          Shifts
        </h2>

        <div style={{ ...innerCard, padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
            <thead>
              <tr>
				<th style={{ ...thStyle, width: "40px", textAlign: "center" }}>#</th>
                <th style={thStyle}>Date</th>
                <th style={thStyle}>Client</th>
                <th style={thStyle}>Staff</th>
                <th style={thStyle}>Requested By</th>
                <th style={thStyle}>Time</th>
                <th style={thStyle}>Hours</th>
                <th style={thStyle}>Rate (£)</th>
                <th style={thStyle}>Status</th>
                <th style={{ ...thStyle, textAlign: "right" }}>Actions</th>
              </tr>
            </thead>

            <tbody>
              {[...shifts]
                .sort((a, b) => new Date(b.date) - new Date(a.date))
                .map((shift, i) => (
                  <tr key={shift.id} style={{ background: i % 2 === 0 ? "#ffffff" : "#f9fafb" }}>
				  
					<td style={{...tdStyle, textAlign: "center", fontWeight: 700, color: "#374151", }}> {i + 1} </td>
                    <td style={tdStyle}>{shift.date}</td>
                    <td style={tdStyle}>{shift.clientName}</td>
                    <td style={tdStyle}>{shift.staffName}</td>
                    <td style={tdStyle}>{shift.requestedByName || shift.requestedBy || "-"}</td>
                    <td style={tdStyle}>
                      {shift.startTime} - {shift.endTime}
                    </td>
                    <td style={tdStyle}>{shift.totalHours}</td>
                    <td style={tdStyle}>{shift.rate}</td>

                    <td
                      style={{
                        ...tdStyle,
                        color: getStatusColor(shift.status),
                        fontWeight: 800,
                      }}
                    >
                      <div>{shift.status}</div>

                      {shift.requests && shift.requests.length > 0 && (
                        <div style={{ marginTop: "8px", color: "#374151" }}>
                          <div style={{ fontSize: "12px", fontWeight: 900, color: "#374151" }}>Requested By:</div>
                          <ul style={{ margin: "6px 0 0 14px", padding: 0 }}>
                            {shift.requests.map((r) => (
                              <li key={r.uid} style={{ fontSize: "12px", color: "#6b7280", fontWeight: 700 }}>
                                {r.name}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </td>

                    <td style={{ ...tdStyle, textAlign: "right", whiteSpace: "nowrap" }}>
                      {shift.requests && shift.requests.length > 0 && (
                        <button
                          onClick={() => openAssignModal(shift)}
                          style={{
                            ...btn("#10b981"),
                            padding: "8px 10px",
                            marginBottom: "8px",
                          }}
                        >
                          Approve & Assign
                        </button>
                      )}

                      <button
                        onClick={() => {
                          setNewShift({
                            ...shift,
                            endTime: shift.plannedEndTime || shift.endTime,
                            isOpen: shift.isOpen || shift.status === "Open",
                          });

                          setExtendEndByHour(shift.extendEndByHour || false);
                          setEditing(true);
                        }}
                        style={actionButton("#0ea5e9")}
                        title="Edit"
                      >
                        ✎
                      </button>

                      <button
                        onClick={() => handleDeleteShift(shift.id)}
                        style={actionButton("#ef4444")}
                        title="Delete"
                      >
                        🗑
                      </button>
                    </td>
                  </tr>
                ))}

              {shifts.length === 0 && (
                <tr>
                  <td colSpan="9" style={{ padding: "20px", color: "#6b7280", fontSize: 13 }}>
                    No shifts found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assign Modal */}
      {assignModalOpen && shiftToAssign && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(0,0,0,0.55)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 999,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(420px, 100%)",
              background: "white",
              borderRadius: "16px",
              boxShadow: "0 8px 24px rgba(0,0,0,0.25)",
              padding: "18px",
              color: "#111827",
            }}
          >
            <div style={{ fontSize: 16, fontWeight: 900, marginBottom: 10 }}>
              Assign Shift — {shiftToAssign.clientName}
            </div>

            <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 800, marginBottom: 10 }}>
              Select requester:
            </div>

            <div style={{ display: "grid", gap: 8 }}>
              {(shiftToAssign.requests || []).map((r) => (
                <label
                  key={r.uid}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 10,
                    padding: "10px 12px",
                    borderRadius: "12px",
                    border: "1px solid #e5e7eb",
                    background: "#f9fafb",
                    cursor: "pointer",
                    fontWeight: 800,
                    color: "#374151",
                  }}
                >
                  <input
                    type="radio"
                    name="requester"
                    value={r.uid}
                    checked={selectedRequester?.uid === r.uid}
                    onChange={() => setSelectedRequester(r)}
                  />
                  {r.name}
                </label>
              ))}
            </div>

            <div style={{ display: "flex", marginTop: 16, gap: 10 }}>
              <button onClick={() => setAssignModalOpen(false)} style={{ ...btn("#6b7280"), flex: 1 }}>
                Cancel
              </button>

              <button onClick={assignRequestedShift} style={{ ...btn("#10b981"), flex: 1 }}>
                Assign
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
